import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { SdDialog } from '@solid-design-system/components/dist-versioned';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Dialog } from 'src/app/common/util/dialog';

type NameError = 'empty' | 'duplicate' | null;

@Component({
  selector: 'app-rename-filter-template-dialog',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, TranslocoModule],
  templateUrl: './rename-filter-template-dialog.component.html',
  styleUrl: './rename-filter-template-dialog.component.scss',
})
export class RenameFilterTemplateDialogComponent extends Dialog {
  readonly headline = 'dashboard.renameTemplate.dialog.title';
  readonly cancelLabel = 'dashboard.renameTemplate.dialog.cancel';
  readonly confirmLabel = 'dashboard.renameTemplate.dialog.confirm';
  readonly emptyNameError = 'dashboard.renameTemplate.dialog.emptyName';
  readonly duplicateNameError = 'dashboard.renameTemplate.dialog.duplicateName';

  private readonly translocoService = inject(TranslocoService);

  /** Names of all templates the current user owns, excluding the one being renamed. */
  public readonly existingNames = input<string[]>([]);

  /** Emits when the user confirms the dialog with a valid name. */
  public readonly renamed = output<string>();

  protected readonly saving = signal<boolean>(false);
  /** Local copy of the input text, updated on every keystroke. */
  protected readonly nameValue = signal<string>('');

  /** Snapshot of the other template names, taken once when the dialog opens. */
  protected readonly existingNamesState = signal<string[]>([]);

  /** Current validation error of the name input, or null when the name is valid. */
  readonly error = computed<NameError>(() => {
    const trimmed = this.nameValue().trim();
    if (trimmed.length === 0) {
      return 'empty';
    }
    const isDuplicate = this.existingNamesState().some(
      existing => existing != null && existing !== '' && existing.trim().toLowerCase() === trimmed.toLowerCase()
    );
    return isDuplicate ? 'duplicate' : null;
  });

  @ViewChild('dialog')
  private readonly dialog!: ElementRef<SdDialog>;

  @ViewChild('nameInput')
  private readonly nameInput!: ElementRef<HTMLInputElement>;

  get canSave(): boolean {
    return this.error() === null && !this.saving();
  }

  confirm(): void {
    if (this.saving() || this.error() !== null) {
      return;
    }
    this.saving.set(true);
    this.renamed.emit(this.nameValue().trim());
    this.saving.set(false);
    this.dialog?.nativeElement?.hide();
  }

  cancel(): void {}

  open(name: string): void {
    this.nameValue.set(name);
    this.existingNamesState.set([...this.existingNames()]);
    this.dialog?.nativeElement?.show();
  }

  onNameChanged(event: Event): void {
    this.nameValue.set((event.target as HTMLInputElement).value);
    this.updateValidity();
  }

  private updateValidity(): void {
    const input = this.nameInput?.nativeElement;
    if (typeof input?.setCustomValidity !== 'function') {
      return;
    }
    const message =
      this.error() === 'empty'
        ? this.translocoService.translate(this.emptyNameError)
        : this.error() === 'duplicate'
          ? this.translocoService.translate(this.duplicateNameError)
          : '';
    input.setCustomValidity(message);
    input.reportValidity();
  }

  protected onAfterHide(): void {
    this.nameValue.set('');
    this.saving.set(false);
    this.nameInput?.nativeElement?.setCustomValidity('');
  }
}

